import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, KeyRound, LogIn, Smartphone } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { KonsildentLogo, Footer } from "../components/Brand";
import { Button, ErrorBox, Field, Input } from "../components/kd";

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex flex-1 items-start justify-center px-4 py-10 sm:items-center">
        <div className="w-full max-w-md">
          <div className="mb-6 flex justify-center"><KonsildentLogo className="h-14" /></div>
          <div className="kd-card kd-fade p-6 sm:p-8">{children}</div>
          <p className="mt-4 text-center text-sm text-text-muted">Das Überweiserportal für Fachpraxen</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function TotpStep({ setup, onDone, onExpired }: { setup: any; onDone: (d: any) => void; onExpired?: (msg: string) => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const r = await api.post("/auth/totp/bestaetigen", { code }); onDone(r.data); }
    catch (ex: any) {
      const m = errMsg(ex);
      if (onExpired && (/Fehlversuche|abgelaufen|erneut an/i.test(m))) onExpired(m); else setErr(m);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="totp-form">
      <h1 className="mb-2 flex items-center gap-2"><Smartphone className="h-6 w-6 text-primary" aria-hidden />Zwei-Faktor-Code</h1>
      {setup ? (
        <div className="mb-5" data-testid="totp-setup">
          <p className="mb-3 text-text-muted">Richten Sie die Zwei-Faktor-Authentifizierung ein: Scannen Sie den QR-Code mit einer Authenticator-App (z. B. Google Authenticator) und geben Sie den angezeigten Code ein.</p>
          <p className="mb-3 rounded-lg border border-warning bg-warning-bg p-3 text-sm text-[#7A3E00]" data-testid="totp-new-hint">Haben Sie Konsildent schon in Ihrer Authenticator-App? Dann löschen Sie den alten Eintrag und scannen Sie diesen QR-Code neu. Codes aus dem alten Eintrag funktionieren nicht mehr. Der QR-Code bleibt bis zur erfolgreichen Einrichtung gleich.</p>
          <img src={setup.qr} alt="QR-Code zur Einrichtung der Zwei-Faktor-Authentifizierung" className="mx-auto h-48 w-48 rounded-lg border border-border" />
          <p className="mt-3 text-sm text-text-muted">Manueller Schlüssel:</p>
          <code className="block break-all rounded-md bg-bg p-2 text-sm" data-testid="totp-secret">{setup.secret}</code>
        </div>
      ) : (
        <p className="mb-5 text-text-muted">Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.</p>
      )}
      <ErrorBox testid="totp-error">{err}</ErrorBox>
      <Field label="Code" required htmlFor="totp">
        <Input id="totp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="tracking-[0.3em]" data-testid="totp-input" autoFocus />
      </Field>
      <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6} data-testid="totp-submit-button">Bestätigen</Button>
    </form>
  );
}

function PasswordInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id="pw" type={show ? "text" : "password"} autoComplete="current-password" value={value} onChange={(e) => onChange(e.target.value)} className="pr-12" data-testid="login-password-input" />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"} aria-pressed={show}
        className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-lg text-text-muted hover:text-text" data-testid="login-password-toggle">
        {show ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
      </button>
    </div>
  );
}

export default function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stage, setStage] = useState<"pw" | "totp">("pw");
  const [setup, setSetup] = useState<any>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post("/auth/login", { email, passwort: pw });
      if (r.data.ohne_2fa) { nav(sp.get("weiter") || "/praxis/faelle", { replace: true }); return; }
      if (!r.data.totp_eingerichtet) setSetup((await api.get("/auth/totp/einrichtung")).data);
      setStage("totp");
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout>
      {stage === "totp" ? <TotpStep setup={setup} onExpired={(m) => { setSetup(null); setStage("pw"); setErr(m); }} onDone={(d) => nav(sp.get("weiter") || (d.art === "UEB" ? "/ueberweiser" : "/praxis/faelle"), { replace: true })} /> : (
        <form onSubmit={submit} data-testid="login-form">
          <h1 className="mb-1 flex items-center gap-2"><KeyRound className="h-6 w-6 text-primary" aria-hidden />Anmeldung Fachpraxis</h1>
          <p className="mb-5 text-text-muted">Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an.</p>
          {sp.get("abgelaufen") && <div className="mb-4 rounded-lg border border-border bg-info-bg p-3 text-sm text-info" data-testid="login-expired-info">Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.</div>}
          <ErrorBox testid="login-error">{err}</ErrorBox>
          <Field label="E-Mail-Adresse" required htmlFor="email"><Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></Field>
          <Field label="Passwort" required htmlFor="pw"><PasswordInput value={pw} onChange={setPw} /></Field>
          <Button type="submit" className="w-full" loading={busy} data-testid="login-submit-button"><LogIn className="h-4 w-4" aria-hidden />Anmelden</Button>
          <p className="mt-4 text-center"><Link to="/passwort-vergessen" data-testid="forgot-password-link">Passwort vergessen?</Link></p>
          <p className="mt-2 text-center text-sm"><Link to="/" data-testid="login-home-link">Zur Startseite</Link></p>
          <p className="mt-2 text-center text-sm text-text-muted">Überweiserpraxis ohne Konto? <Link to="/registrieren" data-testid="register-link">Konto anlegen</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}
